import React, { useEffect, useState } from 'react';
import AppButton from '../../../../components/AppButton/AppButton';
import { IOrder } from '../../../../types/interfaces';
import { useGlobalContext } from '../../../../contexts/GlobalContext';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import OrderCompletedModal from '../../../../components/OrderCompletedModal/OrderCompletedModal';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getOrderDetails,
  getOrderItems,
  orderConfirmation,
} from '../../../../services/catalogService';
import Breadcrumb from '../../../../components/BreadCrumb/BreadCrumb';
import { OrderStatus, PaymentMethod } from '../../../../types/enums';
import { downloadDocument } from '../../../../services/edmsService';

interface IOrderDetails {
  orderId: string;
  subTotal: number;
  discount: number;
  tax: number;
  paymentMethod: string;
  status: OrderStatus;
  contactEmail: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  region: string;
  city: string;
  town: string;
  businessName: string;
  paymentDescription: string;
  paymentDocument: string;
}

const OrderDetailsPage = () => {
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false);
  const [items, setItems] = useState<any>();
  const [orderItem, setOrderItem] = useState<IOrderDetails | null>(null);
  const [approvalState, setApprovalState] = useState<OrderStatus>(OrderStatus.NEW);

  const { supplierId, setSupplierId } = useGlobalContext();
  const discount = 0;
  const shippingFee = 0;
  const { orderId } = useParams<string>();
  const location = useLocation();
  const { paymentMethod, showApprovalActions } = location.state || {};
  const navigate = useNavigate();
  const paths = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Catalogue', path: '/catalogue' },
    {
      label: 'Order',
      path: '/catalogue',
    },
  ];

  const getOrder = async () => {
    if (!orderId) return;
    const response = await getOrderDetails(orderId);
    if (response?.isSuccess) {
      setOrderItem(response.data);
    } else {
      toast.error(response?.errorMessage);
    }
  };

  const getCartItems = async () => {
    if (!orderId) return;
    const response = await getOrderItems(orderId);
    console.log('Order Details:', response);
    if (response?.isSuccess) {
      setItems(response.data);
    } else {
      toast.error(response?.errorMessage);
    }
  };

  const handleAction = (state: OrderStatus) => {
    setApprovalState(state);
    setShowConfirmModal(true);
  };

  const handleOrderConfirmation = async (state: OrderStatus) => {
    if (state == OrderStatus.NEW) return;
    const payload = {
      orderId: orderId,
      orderStatus: state,
    };
    const response = await orderConfirmation(payload);
    if (response?.isSuccess) {
      toast.success(response.data?.message);
    } else {
      toast.error(response?.errorMessage);
    }

    navigate(`/catalogue`);
  };

  const handlePaymentDocumentDownload = async () => {
    const request = {
      documentId: orderId,
      module: 'General',
    };
    if (!orderItem?.paymentDocument || !orderId) return;
    const response = await downloadDocument(request);
    if (response) {
      const fileExtension = orderItem?.paymentDocument.split('.').pop()?.toLowerCase();
      if (fileExtension === 'pdf') {
        // Open the PDF in a new tab
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');
        if (!newTab) {
          toast.error('Popup blocked. Please allow popups to view the document.');
        }
      } else {
        // Download the file
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `payment-${orderItem?.businessName}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      toast.error('Failed to view document');
    }
  };

  useEffect(() => {
    getOrder();
    getCartItems();
  }, [orderId]);

  const data = {
    firstName: 'Samuel',
    lastName: 'McQueen',
    email: 'Samuel_McQueen@gmail.com',
    phoneNumber: '+1 (555) 000-0000',
    country: 'United States of America',
    officeAddress: 'POX 23, Lewisville Avenue',
    city: 'POX 23, Lewisville Avenue',
    suburb: 'Roswell',
    businessName: 'Shell USA',
    municipality: 'Atlanta',
    paymentMethod: 'Offline Mode',
    paymentStatus: 'Unpaid',
    paymentDescription:
      'One unit of a pressurized outer shell encloses a collection of tubes. One fluid flows through the tubes, while the other flows over the tubes.',
    uploadedFile: 'Payment Receipt.pdf',
  };
  return (
    <>
      <div className="px-8 py-4">
        <Breadcrumb paths={paths} />
        <h2 className="text-2xl">Order Details</h2>
        <p className="mt-2 text-gray-600">Track and manage your orders</p>
        {paymentMethod === PaymentMethod.OFFLINE && showApprovalActions && (
          <div className="mt-5 flex justify-between rounded-lg border border-gray-100 bg-white px-6 pb-4 pt-4 shadow-sm">
            <div>
              <h2 className="text-lg">Order Actions</h2>
              <p className="text-lg">
                You can approve, reject, or cancel this order based on the current status.
              </p>
            </div>
            <div className="flex gap-8">
              <AppButton
                title={'Reject'}
                onClick={() => handleAction(OrderStatus.REJECTED)}
                className="rounded-lg bg-gray-100 px-10 py-2 text-lg font-semibold text-red-700"
              />
              <AppButton
                title={'Confirm'}
                onClick={() => handleAction(OrderStatus.PAID)}
                className="rounded-lg bg-green-600 px-6 py-2 text-lg font-semibold text-white"
              />
              <AppButton
                title={'Cancel'}
                onClick={() => handleAction(OrderStatus.CANCELLED)}
                className="rounded-lg bg-yellow-500 px-8 py-2 text-lg font-semibold text-white"
              />
            </div>
          </div>
        )}
        {showConfirmModal && (
          <ConfirmationModal
            title={
              approvalState === OrderStatus.PAID
                ? 'Approve Payment?'
                : approvalState === OrderStatus.CANCELLED
                  ? 'Cancel Order?'
                  : 'Reject Order?'
            }
            desc={
              approvalState === OrderStatus.PAID
                ? 'By clicking confirm, you verify that you have received payment.'
                : approvalState === OrderStatus.CANCELLED
                  ? 'Are you sure you want to cancel this order?'
                  : 'Are you sure you want to reject the order?'
            }
            appBtnConfirm={
              approvalState === OrderStatus.PAID
                ? 'Yes, confirm'
                : approvalState === OrderStatus.CANCELLED
                  ? 'Yes, cancel'
                  : 'Yes, reject'
            }
            appBtnCancel={'Cancel'}
            confirmBgColor={
              approvalState === OrderStatus.PAID
                ? 'bg-green-500'
                : approvalState === OrderStatus.CANCELLED
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }
            onConfirm={() => handleOrderConfirmation(approvalState)}
            onCancel={() => {
              setShowConfirmModal(false);
              setApprovalState(OrderStatus.NEW);
            }}
          />
        )}
        {showOrderConfirmModal && (
          <OrderCompletedModal
            orderNumber={''}
            orderText="This order is now active"
            orderStatus="Order Confirmed"
            appBtnOkay="Okay"
            onView={() => toast.info('view order')}
            onBack={() => setShowOrderConfirmModal(false)}
          />
        )}
        {/* Order Summary */}
        <div className="mt-5 rounded-lg border border-gray-100 bg-white px-10 pb-4 pt-4 shadow-sm">
          <div className="mt-2">
            <h2 className="text-2xl">Order Summary</h2>
          </div>
          <div className="mt-10 flex w-full justify-between">
            <div className="w-[40%]">
              {items?.itemModels?.length > 0 &&
                items.itemModels?.map((item: IOrder) => (
                  <div key={item.orderId} className="mt-8 flex justify-between">
                    <div>
                      <img
                        src={
                          item.sharepointPath ||
                          `${process.env.PUBLIC_URL}/assets/images/compressor.svg`
                        }
                        alt={item.descriptions || 'product'}
                        className={`h-36 w-full rounded-t-lg object-cover`}
                      />
                    </div>
                    <div className="mt-4 flex flex-col gap-4">
                      <p className="text-lg font-semibold text-gray-600">{item.descriptions}</p>
                      <p className="text-lg font-semibold">${item.cost?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="mt-4 text-gray-600">{item.quantity}x</p>
                    </div>
                  </div>
                ))}
            </div>
            <div className="w-[45%] pr-10">
              <div className="mb-10 flex justify-between">
                <div className="space-y-3 text-lg">
                  <p>Subtotal</p>
                  <p>Shipping</p>
                  <p>Discount</p>
                  <p>Tax</p>
                </div>
                <div className="space-y-3 text-lg">
                  <p className="font-bold">${items?.subTotal}</p>
                  <p className="font-semibold text-green-500">
                    {shippingFee <= 0 ? 'FREE' : shippingFee}
                  </p>
                  <p className="">{discount <= 0 ? '-' : discount}</p>
                  <p>{items?.vatTotal}</p>
                </div>
              </div>
              <hr className="text-gray-400" />
              <div>
                <div className="mb-8 mt-5 flex items-center justify-between">
                  <p className="text-xl">Total</p>
                  <p className="text-xl font-semibold">${items?.vatTotal + items?.subTotal}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Shipping Information */}
        <div className="mt-5 rounded-lg border border-gray-100 bg-white px-10 pb-8 pt-4 shadow-sm">
          <h2 className="mb-6 text-xl">Shipping Information</h2>
          <div className="mb-10 ml-24 grid grid-cols-2 gap-4">
            <div>
              <p className="text-lg font-medium text-gray-500">First Name</p>
              <p className="text-black">{orderItem?.firstName}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Business Name</p>
              <p className="text-black">{orderItem?.businessName}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Last Name</p>
              <p className="text-black">{orderItem?.lastName}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Municipality/Region</p>
              <p className="text-black">{orderItem?.region}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Email</p>
              <p className="text-black">{orderItem?.contactEmail}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Suburb or Town</p>
              <p className="text-black">{orderItem?.town}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Country</p>
              <p className="text-black">{orderItem?.country}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Phone Number</p>
              <p className="text-black">{orderItem?.phoneNumber}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Office Address Line 1</p>
              <p className="text-black">{orderItem?.addressLine1}</p>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-500">Post Administrative/City</p>
              <p className="text-black">{orderItem?.city}</p>
            </div>
          </div>
          {/* Payment Method */}
          <div>
            <h2 className="mb-4 text-xl">Payment Method</h2>
            <div className="ml-24">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="mt-4 text-lg font-medium text-gray-500">Method</p>
                  <p className="text-black">{orderItem?.paymentMethod}</p>
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-500">Status</p>
                  <p
                    className={`inline-block rounded-full px-4 py-1 font-bold text-white ${
                      orderItem?.status === OrderStatus.UNPAID ? 'bg-red-500' : 'bg-green-500'
                    }`}
                  >
                    {/* {orderItem?.status === 'New' ? 'Unpaid' : 'Paid'} */}
                    {orderItem?.status}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-lg font-medium text-gray-500">
                  Description or Reason for Payment
                </p>
                <p className="text-black">{orderItem?.paymentDescription}</p>
              </div>
              <div className="mt-4">
                <p className="text-lg font-medium text-gray-500">Upload Proof of Payment</p>
                <div className="mt-2 flex items-center gap-3">
                  <p className="w-[50%] rounded-lg bg-gray-100 px-3 py-2">{data.uploadedFile}</p>
                  <p
                    onClick={handlePaymentDocumentDownload}
                    className="cursor-pointer text-lg text-green-600 underline"
                  >
                    Download
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetailsPage;
